import { useState } from 'react';
import useAsync from 'react-use/lib/useAsync';
import { useApi } from '@backstage/core-plugin-api';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  CircularProgress,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { argoWorkflowsApiRef } from '../../api/ArgoWorkflowsApi';
import { WorkflowTemplate } from '../../api/types';

interface SubmitWorkflowDialogProps {
  namespace: string;
  open: boolean;
  onClose: () => void;
  onSubmitted: (namespace: string, name: string) => void;
}

export function SubmitWorkflowDialog({
  namespace,
  open,
  onClose,
  onSubmitted,
}: SubmitWorkflowDialogProps) {
  const api = useApi(argoWorkflowsApiRef);
  const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);
  const [params, setParams] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const templatesState = useAsync(
    () => (open ? api.listWorkflowTemplates(namespace) : Promise.resolve([])),
    [namespace, open],
  );

  const handleTemplateChange = (name: string) => {
    const tmpl = templatesState.value?.find(t => t.name === name) ?? null;
    setSelectedTemplate(tmpl);
    if (tmpl?.parameters) {
      const defaults: Record<string, string> = {};
      for (const p of tmpl.parameters) {
        if (p.value) defaults[p.name] = p.value;
      }
      setParams(defaults);
    } else {
      setParams({});
    }
  };

  const handleSubmit = async () => {
    if (!selectedTemplate) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await api.submitWorkflow({
        namespace,
        templateName: selectedTemplate.name,
        parameters: Object.keys(params).length > 0 ? params : undefined,
      });
      onSubmitted(result.namespace, result.name);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Submit Workflow</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" style={{ marginBottom: 12 }}>
            {error}
          </Alert>
        )}
        {templatesState.loading && <CircularProgress size={24} />}
        {templatesState.error && (
          <Alert severity="error">Failed to load templates: {templatesState.error.message}</Alert>
        )}
        {templatesState.value && (
          <>
            <FormControl fullWidth variant="outlined" size="small" style={{ marginBottom: 16, marginTop: 8 }}>
              <InputLabel>Template</InputLabel>
              <Select
                value={selectedTemplate?.name ?? ''}
                onChange={e => handleTemplateChange(e.target.value as string)}
                label="Template"
              >
                {templatesState.value.map(t => (
                  <MenuItem key={t.name} value={t.name}>{t.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedTemplate?.parameters && selectedTemplate.parameters.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {selectedTemplate.parameters.map(p => (
                  <TextField
                    key={p.name}
                    label={p.name}
                    helperText={p.description}
                    size="small"
                    variant="outlined"
                    value={params[p.name] ?? ''}
                    onChange={e => setParams(prev => ({ ...prev, [p.name]: e.target.value }))}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={!selectedTemplate || submitting}
        >
          {submitting ? 'Submitting...' : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
